import { useAtom, useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { EmptySearchResults, Pagination, SkeletonText } from '@ekhodealer/ekho-common/rev';

import { Table, type TableColumn } from '@common/components/Table/Table';
import {
	formatReportDate,
	formatReportNumber,
	getVehiclePaymentStatusTag,
} from '@common/lib/reports';

import {
	currentPageAtom,
	isGeneratingOrdersReportAtom,
	itemsPerPageAtom,
	type OrderReportItem,
	ordersReportDataAtom,
} from '../../atoms/ordersAtoms';

import './styles.css';

const columns: TableColumn<OrderReportItem>[] = [
	{
		id: 'placedOrderId',
		title: 'Placed order ID',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontFamily: 'monospace' }}>{item.placedOrderId}</div>
		),
	},
	{
		id: 'uid',
		title: 'ID',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontFamily: 'monospace' }}>{item.uid}</div>
		),
	},
	{
		id: 'formattedVin',
		title: 'VIN',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontFamily: 'monospace' }}>{item.formattedVin}</div>
		),
	},
	{
		id: 'customer',
		title: 'Customer',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>{item.customer}</div>
		),
	},
	{
		id: 'date',
		title: 'Date',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>{formatReportDate(item.date)}</div>
		),
	},
	{
		id: 'deliveryDate',
		title: 'Delivery date',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				{formatReportDate(item.deliveryDate)}
			</div>
		),
	},
	{
		id: 'dispatchDate',
		title: 'Dispatch date',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				{formatReportDate(item.dispatchDate)}
			</div>
		),
	},
	{
		id: 'basePrice',
		title: 'Base price',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.basePrice)}
			</div>
		),
	},
	{
		id: 'addOns',
		title: 'Add ons',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.addOns)}
			</div>
		),
	},
	{
		id: 'totalDiscount',
		title: 'Total discount',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.totalDiscount)}
			</div>
		),
	},
	{
		id: 'vehiclePriceNoFees',
		title: 'Vehicle price no fees',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.vehiclePriceNoFees)}
			</div>
		),
	},
	{
		id: 'taxes',
		title: 'Taxes',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.taxes)}
			</div>
		),
	},
	{
		id: 'altGovFees',
		title: 'Alt gov fees',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.altGovFees)}
			</div>
		),
	},
	{
		id: 'deliveryFee',
		title: 'Delivery fee',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.deliveryFee)}
			</div>
		),
	},
	{
		id: 'pickUpFee',
		title: 'Pick up fee',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.pickUpFee)}
			</div>
		),
	},
	{
		id: 'buyerCoveredProcessingFees',
		title: 'Buyer covered processing fees',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.buyerCoveredProcessingFees)}
			</div>
		),
	},
	{
		id: 'totalDue',
		title: 'Total due',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 500 }}>
				${formatReportNumber(item.totalDue)}
			</div>
		),
	},
	{
		id: 'paidByCustomer',
		title: 'Paid by customer',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.paidByCustomer)}
			</div>
		),
	},
	{
		id: 'paidByCustomerOffPlatform',
		title: 'Paid off platform',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.paidByCustomerOffPlatform)}
			</div>
		),
	},
	{
		id: 'paidByLender',
		title: 'Paid by lender',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.paidByLender)}
			</div>
		),
	},
	{
		id: 'totalPaid',
		title: 'Total paid',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.totalPaid)}
			</div>
		),
	},
	{
		id: 'invoicePrice',
		title: 'Invoice price',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.invoicePrice)}
			</div>
		),
	},
	{
		id: 'oemCollectedShippingFees',
		title: 'Client collected shipping fees',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.oemCollectedShippingFees)}
			</div>
		),
	},
	{
		id: 'grossOemOwed',
		title: 'Gross client owed',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatReportNumber(item.grossOemOwed)}
			</div>
		),
	},
	{
		id: 'vehiclePaymentStatus',
		title: 'Vehicle payment status',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				{getVehiclePaymentStatusTag(item.vehiclePaymentStatus)}
			</div>
		),
	},
	{
		id: 'paymentNotes',
		title: 'Payment notes',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>{item.paymentNotes || '--'}</div>
		),
	},
	{
		id: 'disputeStatus',
		title: 'Dispute status',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>{item.disputeStatus || '--'}</div>
		),
	},
	{
		id: 'externalStatus',
		title: 'External status',
		renderCell: (item: OrderReportItem) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>{item.externalStatus || '--'}</div>
		),
	},
];

function ReportsOrdersTable() {
	const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
	const [itemsPerPage, setItemsPerPage] = useAtom(itemsPerPageAtom);
	const reportData = useAtomValue(ordersReportDataAtom);
	const isPending = useAtomValue(isGeneratingOrdersReportAtom);

	const sortedOrders = useMemo(() => {
		if (!reportData) return [];
		const dateValue = (item: OrderReportItem) => {
			if (!item.date) return 0;
			return typeof item.date === 'string' ? parseInt(item.date, 10) : item.date;
		};
		return [...reportData].sort((a, b) => dateValue(b) - dateValue(a));
	}, [reportData]);

	const paginatedOrders = useMemo(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		return sortedOrders.slice(startIndex, endIndex);
	}, [sortedOrders, currentPage, itemsPerPage]);

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handleItemsPerPageChange = (newItemsPerPage: number) => {
		setItemsPerPage(newItemsPerPage);
		setCurrentPage(1);
	};

	// Show skeleton when loading - always show skeleton during loading regardless of previous data
	if (isPending) {
		// Create skeleton table rows
		const skeletonRows: Array<{ id: string }> = Array.from({ length: 5 }, (_, index) => ({
			id: `skeleton-${index}`,
		}));

		const skeletonColumns: TableColumn<{ id: string }>[] = columns.map((col) => ({
			...col,
			renderCell: () => <SkeletonText numberOfLines={1} textSize='body' />,
		}));

		return (
			<Table
				className='w-full orders-table'
				columns={skeletonColumns}
				items={skeletonRows}
				getItemId={(item: { id: string }) => item.id}
				aria-label='Loading orders table'
			/>
		);
	}

	// Initial state: no report generated yet - show nothing
	if (reportData === null) {
		return null;
	}

	// If we have data (even empty array), check for empty state
	if (sortedOrders.length === 0) {
		return <EmptySearchResults title='No matching results found' />;
	}

	// Report has results - show table
	return (
		<Table
			className='w-full orders-table'
			columns={columns}
			items={paginatedOrders}
			getItemId={(item: OrderReportItem) => item.uid}
			aria-label='Orders table'
			stickyRightCount={1}
			belowTableContent={
				<Pagination
					variant='contained'
					currentPage={currentPage}
					itemsPerPage={itemsPerPage}
					totalItems={sortedOrders.length}
					itemsPerPageOptions={[10, 25, 50, 100]}
					onNavigateTo={handlePageChange}
					onItemsPerPageChange={handleItemsPerPageChange}
					aria-label='Orders pagination'
				/>
			}
		/>
	);
}

export default ReportsOrdersTable;
